import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Users, 
  MessageSquare, 
  TrendingUp,
  Plus,
  Eye,
  Edit,
  BarChart3
} from 'lucide-react';
import { productsAPI, adminAPI, contactRequestsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [productStats, setProductStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [contactStats, setContactStats] = useState(null);
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [productsResponse, adminResponse, contactResponse, recentProductsResponse] = await Promise.all([
        productsAPI.getStats(),
        adminAPI.getStats(),
        contactRequestsAPI.getAll(),
        productsAPI.getAllAdmin({ limit: 5, sortBy: 'createdAt', sortOrder: 'desc' })
      ]);

      setProductStats(productsResponse.data.data);
      setAdminStats(adminResponse.data.data);
      
      // Process contact requests data
      const contacts = contactResponse.data.data.requests || [];
      setContactStats({
        total: contacts.length,
        new: contacts.filter(c => c.status === 'new').length,
        read: contacts.filter(c => c.status === 'read').length,
        replied: contacts.filter(c => c.status === 'replied').length
      });
      
      setRecentProducts(recentProductsResponse.data.data.products);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      name: 'Total Products',
      value: productStats?.totalProducts || 0,
      change: `+${productStats?.recentProducts || 0} this week`,
      changeType: 'positive',
      icon: Package,
      href: '/admin/products',
      color: 'bg-primary'
    },
    {
      name: 'Active Products',
      value: productStats?.activeProducts || 0,
      change: `${Math.round(((productStats?.activeProducts || 0) / (productStats?.totalProducts || 1)) * 100)}% of total`,
      changeType: 'neutral',
      icon: TrendingUp,
      href: '/admin/products',
      color: 'bg-secondary'
    },
    {
      name: 'Contact Requests',
      value: contactStats?.total || 0,
      change: `${contactStats?.new || 0} new`,
      changeType: contactStats?.new > 0 ? 'positive' : 'neutral',
      icon: MessageSquare,
      href: '/admin/contact-requests',
      color: 'bg-primary'
    },
    {
      name: 'Admin Users',
      value: adminStats?.totalUsers || 0,
      change: `+${adminStats?.recentUsers || 0} this week`,
      changeType: 'positive',
      icon: Users,
      href: '/admin/users',
      color: 'bg-secondary'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[#F5F1EB]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EB] p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <img 
              src="/logo.svg" 
              alt="COT Logo" 
              className="h-12 w-auto"
            />
            <div>
              <h1 className="text-3xl font-bold text-secondary font-wix">Dashboard</h1>
              <p className="text-secondary/70 font-wix">Welcome to COT Admin Panel</p>
            </div>
          </div>
          <Link
            to="/admin/products/new"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add New Product</span>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="card group hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-secondary font-wix mb-2">
                    {stat.name}
                  </h3>
                  <p className="text-3xl font-bold text-primary font-wix mb-1">
                    {stat.value}
                  </p>
                  <p className={`text-sm font-wix ${
                    stat.changeType === 'positive' ? 'text-green-600' : 
                    stat.changeType === 'negative' ? 'text-red-600' : 'text-secondary/60'
                  }`}>
                    {stat.change}
                  </p>
                </div>
                <div className={`${stat.color} p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent Products */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-primary p-3 rounded-2xl">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-secondary font-wix">Recent Products</h3>
          </div>
          <Link
            to="/admin/products"
            className="text-primary hover:text-primary/80 font-wix font-medium transition-colors duration-300"
          >
            View All →
          </Link>
        </div>
          
        {recentProducts.length > 0 ? (
          <div className="space-y-4">
            {recentProducts.map((product) => (
              <div key={product._id} className="bg-[#F5F1EB] rounded-2xl p-4 hover:bg-[#F5F1EB]/80 transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        className="h-16 w-16 rounded-2xl object-cover"
                        src={product.image}
                        alt={product.partsName}
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-secondary font-wix mb-1">
                        {product.partsName}
                      </h4>
                      <p className="text-secondary/70 font-wix mb-2">
                        {product.typeOfEquipment?.name || product.typeOfEquipment} • {product.brand?.name || product.brand}
                      </p>
                      <p className="text-sm text-secondary/60 font-wix">
                        Part Number: {product.partsNumber}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full font-wix ${
                      product.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <div className="flex space-x-2">
                      <Link
                        to={`/admin/products/${product._id}`}
                        className="p-2 text-primary hover:bg-primary/10 rounded-xl transition-colors duration-300"
                        title="View"
                      >
                        <Eye className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/admin/products/${product._id}/edit`}
                        className="p-2 text-secondary hover:bg-secondary/10 rounded-xl transition-colors duration-300"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-primary/10 p-6 rounded-2xl inline-block mb-6">
              <Package className="h-16 w-16 text-primary mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-secondary font-wix mb-2">No Products</h3>
            <p className="text-secondary/70 font-wix mb-6">
              Get started by creating a new product to manage your inventory
            </p>
            <Link
              to="/admin/products/new"
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add New Product</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
